import React from "react";

import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { isViteDevServer } from "@/lib/environment";
import { BrowserRouter, Routes, Route } from "react-router";
import * as Sentry from "@sentry/react";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { TenantLogoProvider } from "@/contexts/TenantLogoContext";
import { TenantThemeProvider } from "@/contexts/TenantThemeContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { SessionTimeoutWarning } from "@/components/auth/SessionTimeoutWarning";
import { InactivityWarning } from "@/components/auth/InactivityWarning";
import { CookieConsentBanner } from "@/components/cookie/CookieConsentBanner";
import { RouteSuspenseFallback } from "@/components/layout/RouteSuspenseFallback";

// Eager: Sofort benoetigte Seiten (Auth, Landing, Home, Tasks)
import HomeRoute from "@/components/HomeRoute";
import Tasks from "./pages/Tasks";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import OAuthGraphCallback from "./pages/OAuthGraphCallback";
import NotFound from "./pages/NotFound";
import Landing from "./pages/Landing";
import Imprint from "./pages/Imprint";
import Privacy from "./pages/Privacy";

// InspectionRedirect: kleiner Redirect (named export) — eager.
import { InspectionRedirect } from "./components/inspection/InspectionRedirect";

// Nach Login geladene + Admin-Seiten — LAZY (Route-Code-Splitting, TES-1653): raus aus dem
// Main-Bundle in je eigene, on-demand geladene Chunks. Verhalten unveraendert; eine Suspense-
// Boundary in AppLayout (Shell bleibt stehen) plus eine Top-Level-Boundary (fuer Nicht-Layout-
// Lazy-Routes) faengt den kurzen Lade-Tick pro Route ab. Entry-Pages (Landing/Auth/HomeRoute/
// Tasks) bleiben oben EAGER fuer schnelles FCP.
/* v8 ignore start -- reine Lazy-Import-Wiring (TES-1653): die () => import()-Arrows haben keine
   testbare Logik (die Pages selbst sind via E2E + eigene Unit-Tests abgedeckt). Ohne dies zaehlt
   der v8-Provider jede Arrow als uncovered Function -> Coverage-Ratchet regrediert. */
const Checklists = React.lazy(() => import("./pages/Checklists"));
const ChecklistEditor = React.lazy(() => import("./components/checklist/ChecklistEditor"));
const Inspections = React.lazy(() => import("./pages/Inspections"));
const Schedules = React.lazy(() => import("./pages/Schedules"));
const MachineInspectionPlans = React.lazy(() => import("./pages/MachineInspectionPlans"));
const InspectionExecution = React.lazy(() => import("./pages/InspectionExecution"));
const InspectionPreview = React.lazy(() => import("./pages/InspectionPreview"));
const Defects = React.lazy(() => import("./pages/Defects"));
const SapStammdatenSync = React.lazy(() => import("./pages/SapStammdatenSync"));
const History = React.lazy(() => import("./pages/History"));
const Notifications = React.lazy(() => import("./pages/Notifications"));
const Profile = React.lazy(() => import("./pages/Profile"));
const ViewImage = React.lazy(() => import("./pages/ViewImage"));
const UserGuide = React.lazy(() => import("./pages/UserGuide"));

const UserManagement = React.lazy(() => import("./pages/UserManagement"));
const TenantSettings = React.lazy(() => import("./pages/TenantSettings"));
const InspectionPositions = React.lazy(() => import("./pages/InspectionPositions"));
const Machines = React.lazy(() => import("./pages/Machines"));
const PdfProfiles = React.lazy(() => import("./pages/PdfProfiles"));
const RolePermissions = React.lazy(() => import("./pages/RolePermissions"));
const TeamsManagement = React.lazy(() => import("./pages/TeamsManagement"));
const GroupsManagement = React.lazy(() => import("./pages/GroupsManagement"));
const RlsPoliciesOverview = React.lazy(() => import("./pages/RlsPoliciesOverview"));
const RlsTestCoverage = React.lazy(() => import("./pages/RlsTestCoverage"));
const SentryTraces = React.lazy(() => import("./pages/SentryTraces"));
const DevTools = React.lazy(() => import("./pages/DevTools"));
const MemoryDocs = React.lazy(() => import("./pages/MemoryDocs"));
const SetupStatus = React.lazy(() => import("./pages/SetupStatus"));
const AdminUsers = React.lazy(() => import("./pages/AdminUsers"));
const UiPatterns = React.lazy(() => import("./pages/admin/UiPatterns"));
const GitHubActions = React.lazy(() => import("./pages/admin/GitHubActions"));
const EdgeFunctions = React.lazy(() => import("./pages/admin/EdgeFunctions"));
const SecretsDocs = React.lazy(() => import("./pages/admin/SecretsDocs"));
const ApiDocs = React.lazy(() => import("./pages/admin/ApiDocs"));
const NotificationOverview = React.lazy(() => import("./pages/admin/NotificationOverview"));
const AiSettings = React.lazy(() => import("./pages/admin/AiSettings"));
const SsoSettings = React.lazy(() => import("./pages/admin/SsoSettings"));
const ExternalAccess = React.lazy(() => import("./pages/admin/ExternalAccess"));
const ConflictResolution = React.lazy(() => import("./pages/admin/ConflictResolution"));
const Environments = React.lazy(() => import("./pages/admin/Environments"));
const AuditTrailDashboard = React.lazy(() => import("./pages/admin/AuditTrailDashboard"));
const TemplateStewards = React.lazy(() => import("./pages/admin/TemplateStewards"));
const DefectAutomationRules = React.lazy(() => import("./pages/admin/DefectAutomationRules"));
const WorkflowBuilder = React.lazy(() => import("./pages/admin/WorkflowBuilder"));
const E2eTestCoverage = React.lazy(() => import("./pages/E2eTestCoverage"));
const OAuthConsent = React.lazy(() => import("./pages/OAuthConsent"));
// Dev-only UI-kit harness (TES-1878) — mock data, no auth/Supabase; route gated on DEV.
const UiKitDemo = React.lazy(() => import("./components/dev/UiKitDemo"));
/* v8 ignore stop */

// Lazy load devtools only in development
const ReactQueryDevtoolsLazy = React.lazy(() =>
  import("@tanstack/react-query-devtools").then((mod) => ({
    default: mod.ReactQueryDevtools,
  })),
);

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: "offlineFirst", // Offline-First: pausiert statt Fehlerschleife
      staleTime: 30_000, // 30 Sekunden - Daten gelten als frisch
      gcTime: 5 * 60 * 1000, // 5 Minuten - Garbage Collection Zeit
      refetchOnWindowFocus: false, // Verhindert unnötige Refetches
      retry: 1, // Nur 1 Retry bei Fehlern
    },
    mutations: {
      networkMode: "offlineFirst",
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <RealtimeProvider>
              <TenantLogoProvider>
                <TenantThemeProvider>
                  <SessionTimeoutWarning />
                  <InactivityWarning />

                  <React.Suspense fallback={<RouteSuspenseFallback />}>
                    <SentryRoutes>
                      <Route path="/landing" element={<Landing />} />
                      {import.meta.env.DEV ? (
                        <Route path="/dev/ui-kit" element={<UiKitDemo />} />
                      ) : null}
                      <Route path="/imprint" element={<Imprint />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route path="/oauth/graph-callback" element={<OAuthGraphCallback />} />
                      <Route path="/oauth/consent" element={<OAuthConsent />} />
                      <Route
                        path="/view-image"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <ViewImage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/"
                        element={
                          <AppLayout>
                            <HomeRoute />
                          </AppLayout>
                        }
                      />
                      <Route
                        path="/tasks"
                        element={
                          <ProtectedRoute
                            requiredRoles={[
                              "qualitaetsmanager",
                              "mangelbearbeiter",
                              "tester",
                              "qm_admin",
                            ]}
                          >
                            <AppLayout>
                              <Tasks />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      {/* ADR-CHK-002 (TES-1535): super_admin in admin konsolidiert. admin ist
                      Plattform-Admin OHNE operativen Zugriff und bleibt aus den operativen
                      Ansichten ausgeschlossen (Datenzugriff regelt RLS). */}
                      <Route
                        path="/checklists"
                        element={
                          <ProtectedRoute excludedRoles={["admin", "mangelbearbeiter"]}>
                            <AppLayout>
                              <Checklists />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/checklists/:id"
                        element={
                          <ProtectedRoute requiredRoles={["qualitaetsmanager"]}>
                            <AppLayout>
                              <ChecklistEditor />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/inspections"
                        element={
                          <ProtectedRoute excludedRoles={["admin", "mangelbearbeiter"]}>
                            <AppLayout>
                              <Inspections />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/inspections/:id"
                        element={
                          <ProtectedRoute excludedRoles={["admin"]}>
                            <AppLayout>
                              <InspectionRedirect />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/inspections/:id/execute"
                        element={
                          <ProtectedRoute requiredRoles={["qualitaetsmanager", "tester"]}>
                            <AppLayout>
                              <InspectionExecution />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/inspections/:id/preview"
                        element={
                          <ProtectedRoute excludedRoles={["admin"]}>
                            <AppLayout>
                              <InspectionPreview />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/defects"
                        element={
                          <ProtectedRoute excludedRoles={["admin"]}>
                            <AppLayout>
                              <Defects />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      {/* TES-1731: MPP-Maschinen-Planungseinstieg. admin = mpp:view 'none'
                      (Migration 316) → excludedRoles; QM/Tester/Mangelbearbeiter/Kunde lesen,
                      Schreib-Aktionen sind QM-only (useMppPermissions) + RLS-erzwungen. */}
                      <Route
                        path="/mpp"
                        element={
                          <ProtectedRoute excludedRoles={["admin"]}>
                            <AppLayout>
                              <MachineInspectionPlans />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/sap/stammdaten-sync"
                        element={
                          <ProtectedRoute requiredRoles={["qualitaetsmanager"]}>
                            <AppLayout>
                              <SapStammdatenSync />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/history"
                        element={
                          <ProtectedRoute
                            requiredRoles={[
                              "admin",
                              "qualitaetsmanager",
                              "mangelbearbeiter",
                              "tester",
                              "qm_admin",
                            ]}
                          >
                            <AppLayout>
                              <History />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/notifications"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <Notifications />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <Profile />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      {/* TES-2056: qm_admin pflegt hier die Profil-Stammdaten (job_title/department/
                          standort, RPC Mig 374); die Admin-Mutationen der Seite bleiben in der Seite
                          auf admin/super_admin gegated. */}
                      <Route
                        path="/admin/users"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "qm_admin"]}>
                            <AppLayout>
                              <UserManagement />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/positions"
                        element={
                          <ProtectedRoute requiredRoles={["qualitaetsmanager", "qm_admin"]}>
                            <AppLayout>
                              <InspectionPositions />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/machines"
                        element={
                          <ProtectedRoute requiredRoles={["qualitaetsmanager", "qm_admin"]}>
                            <AppLayout>
                              <Machines />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/pdf-profiles"
                        element={
                          <ProtectedRoute requiredRoles={["qualitaetsmanager"]}>
                            <AppLayout>
                              <PdfProfiles />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/schedules"
                        element={
                          <ProtectedRoute
                            requiredRoles={["qualitaetsmanager", "tester", "qm_admin"]}
                          >
                            <AppLayout>
                              <Schedules />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/roles"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <RolePermissions />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/template-stewards"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <TemplateStewards />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/defect-automation"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <DefectAutomationRules />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      {/* TES-1975: qm_admin verwaltet Gruppen; QM/Admin verwalten Teams/Mitglieder
                          (Übergang); tester/mangelbearbeiter sehen die Gruppen read-only. CRUD ist in
                          der Seite gated (canManageGroups/canManageTeams). */}
                      <Route
                        path="/admin/teams"
                        element={
                          <ProtectedRoute
                            requiredRoles={[
                              "admin",
                              "qualitaetsmanager",
                              "qm_admin",
                              "tester",
                              "mangelbearbeiter",
                            ]}
                          >
                            <AppLayout>
                              <TeamsManagement />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      {/* TES-2048 (ADR 0010): standort-first Gruppen-Verwaltung — Gruppen sind die
                          Org-Hülle (team-frei), Rollen-Gate wie /admin/teams (Liste read-only für
                          alle operativen Rollen, CRUD in der Seite qm_admin-gated). */}
                      <Route
                        path="/admin/groups"
                        element={
                          <ProtectedRoute
                            requiredRoles={[
                              "admin",
                              "qualitaetsmanager",
                              "qm_admin",
                              "tester",
                              "mangelbearbeiter",
                            ]}
                          >
                            <AppLayout>
                              <GroupsManagement />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/workflow-builder"
                        element={
                          <ProtectedRoute requiredRoles={["qualitaetsmanager"]}>
                            <AppLayout>
                              <WorkflowBuilder />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/audit-trail"
                        element={
                          <ProtectedRoute requiredRoles={["admin", "qualitaetsmanager"]}>
                            <AppLayout>
                              <AuditTrailDashboard />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/rls-policies"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <RlsPoliciesOverview />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/rls-test-coverage"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <RlsTestCoverage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/sentry-traces"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <SentryTraces />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/dev-tools"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <DevTools />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/memory-docs"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <MemoryDocs />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/ui-patterns"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <UiPatterns />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/github-actions"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <GitHubActions />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/ai-settings"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <AiSettings />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/sso-settings"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <SsoSettings />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/external-access"
                        element={
                          <ProtectedRoute requiredRoles={["qualitaetsmanager"]}>
                            <AppLayout>
                              <ExternalAccess />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/conflicts"
                        element={
                          <ProtectedRoute requiredRoles={["qualitaetsmanager"]}>
                            <AppLayout>
                              <ConflictResolution />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/e2e-coverage"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <E2eTestCoverage />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/edge-functions"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <EdgeFunctions />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/secrets-docs"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <SecretsDocs />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/api-docs"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <ApiDocs />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/setup-status"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <SetupStatus />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/all-users"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <AdminUsers />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/notification-overview"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <NotificationOverview />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/environments"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <Environments />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/tenant-settings"
                        element={
                          <ProtectedRoute requiredRoles={["admin"]}>
                            <AppLayout>
                              <TenantSettings />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/guide"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <UserGuide />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route path="*" element={<NotFound />} />
                    </SentryRoutes>
                  </React.Suspense>
                  <CookieConsentBanner />
                </TenantThemeProvider>
              </TenantLogoProvider>
            </RealtimeProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
    {isViteDevServer() && (
      <React.Suspense fallback={null}>
        <ReactQueryDevtoolsLazy initialIsOpen={false} />
      </React.Suspense>
    )}
  </QueryClientProvider>
);

export default App;
